import { z } from 'zod';
import { PreflightMarketKindSchema } from './market.js';

/**
 * Aggregate read model behind the Pre Flight hub dashboard. One endpoint serves both
 * scopes; `scope` tells the client how to read `trend` and `heatmap.columnKind`
 * instead of making it infer scope from the data.
 */
export const InsightsScopeEnum = z.enum(['ALL_MARKETS', 'SINGLE_MARKET']);
export type InsightsScope = z.infer<typeof InsightsScopeEnum>;

/** All-markets scope pivots the heatmap by market; single-market scope by report. */
export const HeatmapColumnKindEnum = z.enum(['MARKET', 'REPORT']);
export type HeatmapColumnKind = z.infer<typeof HeatmapColumnKindEnum>;

export const InsightsBandSplitSchema = z.object({
    STRONG: z.number().int().nonnegative(),
    NEEDS_REVIEW: z.number().int().nonnegative(),
    AT_RISK: z.number().int().nonnegative(),
});
export type InsightsBandSplit = z.infer<typeof InsightsBandSplitSchema>;

export const InsightsMetricsSchema = z.object({
    reportCount: z.number().int().nonnegative(),
    creativeCount: z.number().int().nonnegative(),
    /** Null when nothing in scope has been scored yet. */
    avgScore: z.number().nullable(),
});
export type InsightsMetrics = z.infer<typeof InsightsMetricsSchema>;

export const InsightsTrendPointSchema = z.object({
    /** Stable series key: a month bucket (`2026-07`) or a report id. */
    key: z.string(),
    label: z.string(),
    date: z.string(),
    avgScore: z.number(),
    creativeCount: z.number().int().nonnegative(),
    /** Set in single-market scope, where one point is one report. */
    reportId: z.string().nullable(),
});
export type InsightsTrendPoint = z.infer<typeof InsightsTrendPointSchema>;

export const InsightsTrendSchema = z.object({
    points: z.array(InsightsTrendPointSchema),
    /** Last point minus first point; null with fewer than two points. */
    delta: z.number().nullable(),
    from: z.string().nullable(),
    to: z.string().nullable(),
});
export type InsightsTrend = z.infer<typeof InsightsTrendSchema>;

/** One scored creative on the 0–100 score rail. */
export const InsightsScoreDotSchema = z.object({
    id: z.string(),
    label: z.string(),
    score: z.number(),
    band: z.enum(['STRONG', 'NEEDS_REVIEW', 'AT_RISK']),
    reportId: z.string(),
    marketId: z.string().nullable(),
});
export type InsightsScoreDot = z.infer<typeof InsightsScoreDotSchema>;

/**
 * Read URLs are signed per response and expire quickly, so the aggregate cannot carry them:
 * previews are fetched for the creatives a rail panel actually shows.
 */
export const InsightsCreativePreviewSchema = z.object({
    assetId: z.string(),
    /** Short-TTL signed read URL for the creative's thumbnail; null when it has none. */
    thumbnailUrl: z.string().nullable(),
    /** Drives the fallback glyph when there is no thumbnail to show. */
    isVideo: z.boolean(),
});
export type InsightsCreativePreview = z.infer<typeof InsightsCreativePreviewSchema>;

/**
 * A preview costs one signature, so a request is capped at the widest panel a rail can
 * open — enough for the largest cluster, not enough to sign a whole tenant's history.
 */
export const MAX_CREATIVE_PREVIEWS = 24;

export const HeatmapColumnSchema = z.object({
    /** Market id or report id, per `columnKind` — the drill-through target. */
    id: z.string(),
    label: z.string(),
    /** Compact header text: the market code, or null for report columns. */
    code: z.string().nullable(),
    /**
     * Catalogue kind for a market column — picks the flag, globe or region glyph.
     * Null on report columns, which have no market identity of their own.
     */
    kind: PreflightMarketKindSchema.nullable(),
    avgScore: z.number().nullable(),
    creativeCount: z.number().int().nonnegative(),
    /**
     * Reports that contributed creatives to this column. Always 1 for report columns;
     * for market columns, how many reports sit behind the average.
     */
    reportCount: z.number().int().nonnegative(),
});
export type HeatmapColumn = z.infer<typeof HeatmapColumnSchema>;

/** Aggregated verdicts for one criterion within one column. */
export const HeatmapCellSchema = z.object({
    passes: z.number().int().nonnegative(),
    failures: z.number().int().nonnegative(),
    partials: z.number().int().nonnegative(),
    /** Misses over verdicts, partials at half weight; null when no verdicts landed. */
    missRate: z.number().nullable(),
});
export type HeatmapCell = z.infer<typeof HeatmapCellSchema>;

export const HeatmapRowSchema = z.object({
    key: z.string(),
    name: z.string(),
    definition: z.string().nullable(),
    weight: z.number(),
    cells: z.array(HeatmapCellSchema),
    passes: z.number().int().nonnegative(),
    failures: z.number().int().nonnegative(),
    partials: z.number().int().nonnegative(),
});
export type HeatmapRow = z.infer<typeof HeatmapRowSchema>;

export const InsightsHeatmapSchema = z.object({
    columnKind: HeatmapColumnKindEnum,
    columns: z.array(HeatmapColumnSchema),
    rows: z.array(HeatmapRowSchema),
});
export type InsightsHeatmap = z.infer<typeof InsightsHeatmapSchema>;

export const InsightsBlockerSchema = z.object({
    code: z.string().nullable(),
    name: z.string(),
    definition: z.string().nullable(),
    weight: z.number(),
    failedCount: z.number().int().nonnegative(),
    partialCount: z.number().int().nonnegative(),
    scoredAssets: z.number().int().nonnegative(),
    impact: z.number(),
});
export type InsightsBlocker = z.infer<typeof InsightsBlockerSchema>;

/**
 * Per-platform rollup for the hub Sources tab. Only platforms that contributed scored
 * creatives in scope appear — unused catalogue platforms stay out of the payload.
 */
export const InsightsPlatformBreakdownSchema = z.object({
    platform: z.enum(['META', 'YOUTUBE', 'SNAPCHAT', 'TIKTOK', 'DV360', 'ADFORM']),
    creativeCount: z.number().int().nonnegative(),
    reportCount: z.number().int().nonnegative(),
    avgScore: z.number().nullable(),
});
export type InsightsPlatformBreakdown = z.infer<typeof InsightsPlatformBreakdownSchema>;

export const PreflightInsightsSchema = z.object({
    scope: InsightsScopeEnum,
    /** The selected market, or null in all-markets scope. */
    marketId: z.string().nullable(),
    bands: InsightsBandSplitSchema,
    metrics: InsightsMetricsSchema,
    trend: InsightsTrendSchema,
    blockers: z.array(InsightsBlockerSchema),
    scoreSpread: z.array(InsightsScoreDotSchema),
    heatmap: InsightsHeatmapSchema,
    /**
     * The same criteria pivoted by report over the scope's most recent reports, oldest first.
     * Always report-pivoted, whatever `heatmap.columnKind` is: criterion trajectories are read
     * report over report, and market columns have no order to read as time. Overlaps `heatmap`
     * in single-market scope, which is the price of the trends view working in both.
     */
    criterionHistory: InsightsHeatmapSchema,
    /** Platforms that scored creatives in this scope — drives the hub Sources tab. */
    platforms: z.array(InsightsPlatformBreakdownSchema),
});
export type PreflightInsights = z.infer<typeof PreflightInsightsSchema>;
